import React, { useMemo } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import { isEmpty, isNil } from 'lodash-es';

import Skeleton from '../../../../components/Skeleton';
import Tooltip from '../../../../components/Tooltip';
import { format, endOfMonth, isSame } from '../../../../utils/date';
import { createHeatmapColorizer } from '../../../../utils/heatmap';
import { numberToPercentage } from '../../../../utils/formatter';
import { initMaxDate } from '../../configs/chart-config';

const buildYearGroups = (months) =>
  months.reduce((groups, date) => {
    const year = format(date, 'YYYY');
    const last = groups[groups.length - 1];

    if (last && last.year === year) {
      last.count += 1;
    } else {
      groups.push({ year, count: 1 });
    }

    return groups;
  }, []);

const formatValue = (value) => {
  if (isNil(value)) return '\u2014';
  return numberToPercentage(value, { precision: 1 });
};

const SectorsHeatmapTable = ({ months, data }) => {
  const yearGroups = buildYearGroups(months);
  const lastMonthIndex = months.length - 1;

  const { getBackground } = useMemo(() => {
    const allSectorsSeries = data.flatMap((sector) =>
      sector.values.map((value, i) => [months[i], value])
    );
    return createHeatmapColorizer(allSectorsSeries);
  }, [months, data]);

  return (
    <table className="sectors-heatmap">
      <thead>
        <tr className="sectors-heatmap__year-row">
          <th />
          {yearGroups.map(({ year, count }) => (
            <th key={year} colSpan={count}>
              {year}
            </th>
          ))}
        </tr>
        <tr className="sectors-heatmap__month-row">
          <th>
            <div className="d-flex align-items-center justify-content-between">
              <div>Sector</div>
              <div className="d-flex">
                % of Time Spent{' '}
                <Tooltip
                  content="Share of total mobile app time spent in each sector by this user segment"
                  placement="top"
                  colorVariant="light"
                >
                  <span className="hicon-question-circle" />
                </Tooltip>
              </div>
            </div>
          </th>
          {months.map((date, i) => (
            <th key={date}>
              {i === lastMonthIndex && !isSame(initMaxDate, endOfMonth(date)) ? (
                <div className="d-flex align-items-center">
                  {format(date, 'MMM')} (partial){' '}
                  <Tooltip content="Partial month" placement="top" colorVariant="light">
                    <span className="hicon-question-circle" />
                  </Tooltip>
                </div>
              ) : (
                format(date, 'MMM')
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((sector) => (
          <tr key={sector.name}>
            <td className="sector-lead-cell">
              <div className="sector-lead-cell__content">
                <div className="sector-lead-cell__name text-truncate">{sector.name}</div>
                <div className="sector-lead-cell__time-spent">
                  {formatValue(sector.time_spent_pct)}
                </div>
              </div>
            </td>
            {sector.values.map((value, i) => {
              const isPartialCell =
                i === sector.values.length - 1 && !isSame(initMaxDate, endOfMonth(months[i]));
              return (
                <td
                  key={months[i]}
                  style={{ backgroundColor: getBackground(value) }}
                  className={cx({ 'is-partial': isPartialCell })}
                >
                  {formatValue(value)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const emptyArray = [];
const SectorsHeatmap = ({ months, data, isPending }) => {
  if (isPending) {
    return (
      <div className="d-flex p-mdl">
        <Skeleton height={640} />
      </div>
    );
  }

  if (isEmpty(months) || isEmpty(data)) {
    return (
      <div className="d-flex p-mdl">
        <div className="sectors-heatmap__empty flex-grow">No data available</div>
      </div>
    );
  }

  return <SectorsHeatmapTable months={months || emptyArray} data={data || emptyArray} />;
};

// Shared PropTypes
const MonthsPropType = T.arrayOf(T.string);

const SectorPropType = T.shape({
  name: T.string,
  time_spent_pct: T.number,
  values: T.arrayOf(T.number),
});

const SectorsHeatmapTableProps = {
  months: MonthsPropType,
  data: T.arrayOf(SectorPropType),
};

SectorsHeatmapTable.propTypes = SectorsHeatmapTableProps;
SectorsHeatmap.propTypes = {
  ...SectorsHeatmapTableProps,
  isPending: T.bool.isRequired,
};

export default SectorsHeatmap;
